/**
 * RevenueChannelsTab — CEO-editable master list of revenue channels per
 * division. Supports rename, reorder (drag-free: up/down buttons), merge
 * (absorb one channel's key into another's alias list), hide/show.
 *
 * Auto-discover: the GET endpoint upserts any sub_amounts key seen in
 * daily_sales but missing from the master — those rows arrive with a
 * "new" badge so CEO knows Nihal just typed a new channel name.
 */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { ChevronUp, ChevronDown, EyeOff, Eye, Merge, Save, Plus } from 'lucide-react';

const DIVISIONS = [
  { id: 'tdb', label: 'The Doggy Bakery' },
  { id: 'me', label: 'MindEscapes' },
];

export default function RevenueChannelsTab() {
  const [divId, setDivId] = useState('tdb');
  const [chans, setChans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // id of the row being edited
  const [draft, setDraft] = useState({});
  const [mergeFrom, setMergeFrom] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/revenue-channels/${divId}`);
      setChans(r.data.channels || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [divId]);

  const startEdit = (c) => {
    setEditing(c.id);
    setDraft({ key: c.key, label: c.label, aliases: (c.aliases || []).join(', ') });
  };
  const cancelEdit = () => { setEditing(null); setDraft({}); };

  const saveEdit = async (c) => {
    const aliases = draft.aliases
      .split(',').map((s) => s.trim()).filter(Boolean);
    await api.post(`/admin/revenue-channels/${divId}`, {
      id: c.id, key: draft.key, label: draft.label, aliases,
    });
    setEditing(null);
    reload();
  };

  const move = async (idx, dir) => {
    const newOrder = [...chans];
    const target = idx + dir;
    if (target < 0 || target >= newOrder.length) return;
    [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
    setChans(newOrder);
    await api.post(`/admin/revenue-channels/${divId}/reorder`, {
      order: newOrder.map((c) => c.id),
    });
  };

  const toggleHide = async (c) => {
    await api.post(`/admin/revenue-channels/${divId}`, {
      id: c.id, key: c.key, label: c.label,
      aliases: c.aliases, hidden: !c.hidden,
    });
    reload();
  };

  const startMerge = (c) => setMergeFrom(c);
  const completeMerge = async (target) => {
    if (!mergeFrom || mergeFrom.id === target.id) return;
    if (!window.confirm(
      `Merge "${mergeFrom.label}" into "${target.label}"?\n\n` +
      `All future entries with key "${mergeFrom.key}" will collapse into ` +
      `"${target.label}" in the matrix. The "${mergeFrom.label}" row will be ` +
      `removed.\n\nThis is safe — no historical data is deleted.`
    )) return;
    await api.post(`/admin/revenue-channels/${divId}/merge`, {
      source_id: mergeFrom.id, target_id: target.id,
    });
    setMergeFrom(null);
    reload();
  };

  const addNew = async () => {
    const key = window.prompt('Channel key (short, unique, e.g. "Blinkit"):');
    if (!key) return;
    const label = window.prompt('Display label (e.g. "Blinkit (quick-commerce)"):', key) || key;
    await api.post(`/admin/revenue-channels/${divId}`, {
      key: key.trim(), label: label.trim(), aliases: [],
      sort_order: (chans.length + 1) * 10,
    });
    reload();
  };

  return (
    <div className="space-y-4" data-testid="revenue-channels-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[#1B2A4A]">Revenue Channels</h3>
          <p className="text-xs text-[#6B7280] mt-1">
            Master list of channels that appear in the City × Channel matrix.
            Auto-discovers any new <code className="bg-[#F7F5F2] px-1 rounded">sub_amounts</code> key Nihal types.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={divId}
            onChange={(e) => setDivId(e.target.value)}
            className="px-2 py-1.5 text-xs border border-[#E5E1DC] rounded bg-white"
            data-testid="channels-division-select"
          >
            {DIVISIONS.map((d) => (
              <option key={d.id} value={d.id}>{d.label}</option>
            ))}
          </select>
          <button
            onClick={addNew}
            className="px-3 py-1.5 text-xs font-medium rounded bg-[#1B2A4A] text-white flex items-center gap-1"
            data-testid="add-channel-btn"
          >
            <Plus size={12} /> Add channel
          </button>
        </div>
      </div>

      {mergeFrom && (
        <div className="p-3 bg-[#FEF3C7] border border-[#F59E0B] rounded text-xs text-[#92400E]" data-testid="merge-banner">
          Merging <strong>{mergeFrom.label}</strong> → click the <Merge size={12} className="inline mx-1" /> icon on the target channel.
          <button className="ml-2 underline" onClick={() => setMergeFrom(null)}>Cancel</button>
        </div>
      )}

      {loading ? (
        <p className="text-xs text-[#6B7280]">Loading…</p>
      ) : (
        <div className="bg-white rounded-lg border border-[#E5E1DC] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F7F5F2] text-[10px] uppercase tracking-wide text-[#6B7280]">
                <th className="w-10 px-2 py-2"></th>
                <th className="text-left px-3 py-2">Key</th>
                <th className="text-left px-3 py-2">Label</th>
                <th className="text-left px-3 py-2">Aliases (merged in)</th>
                <th className="text-right px-3 py-2 w-56">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F4F6]">
              {chans.map((c, idx) => (
                <tr key={c.id} data-testid={`channel-row-${c.key}`} className={c.hidden ? 'opacity-50' : ''}>
                  <td className="px-2 py-2 text-center">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-[#6B7280] hover:text-[#1B2A4A] disabled:opacity-30" data-testid={`move-up-${c.key}`}><ChevronUp size={14} /></button>
                    <button onClick={() => move(idx, +1)} disabled={idx === chans.length - 1} className="text-[#6B7280] hover:text-[#1B2A4A] disabled:opacity-30" data-testid={`move-down-${c.key}`}><ChevronDown size={14} /></button>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-[#6B7280]">{c.key}</td>
                  <td className="px-3 py-2">
                    {editing === c.id ? (
                      <input
                        value={draft.label}
                        onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-[#E5E1DC] rounded"
                      />
                    ) : (
                      <span className="font-medium text-[#1B2A4A]">{c.label}</span>
                    )}
                    {c.auto_discovered && (
                      <span className="ml-2 text-[9px] uppercase tracking-wider bg-[#FEF3C7] text-[#92400E] px-1.5 py-0.5 rounded" data-testid={`new-badge-${c.key}`}>
                        new
                      </span>
                    )}
                    {c.hidden && <span className="ml-2 text-[9px] uppercase bg-[#F3F4F6] text-[#6B7280] px-1.5 py-0.5 rounded">hidden</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-[#6B7280]">
                    {editing === c.id ? (
                      <input
                        value={draft.aliases}
                        onChange={(e) => setDraft({ ...draft, aliases: e.target.value })}
                        placeholder="comma-separated, e.g. Zomato Hyperpure, Zomato B2B"
                        className="w-full px-2 py-1 text-xs border border-[#E5E1DC] rounded"
                      />
                    ) : (
                      (c.aliases || []).length ? c.aliases.join(', ') : <span className="text-[#D1D5DB]">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {editing === c.id ? (
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => saveEdit(c)} className="text-[11px] px-2 py-1 bg-[#2D8B5E] text-white rounded flex items-center gap-1" data-testid={`save-${c.key}`}><Save size={11} /> Save</button>
                        <button onClick={cancelEdit} className="text-[11px] px-2 py-1 text-[#6B7280]">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => startEdit(c)} className="text-[11px] px-2 py-1 text-[#1B2A4A] hover:bg-[#F7F5F2] rounded" data-testid={`edit-${c.key}`}>Rename</button>
                        {mergeFrom ? (
                          mergeFrom.id === c.id ? (
                            <span className="text-[10px] text-[#92400E]">(source)</span>
                          ) : (
                            <button onClick={() => completeMerge(c)} className="text-[11px] px-2 py-1 text-[#92400E] bg-[#FEF3C7] rounded flex items-center gap-1" data-testid={`merge-into-${c.key}`}><Merge size={11} /> Merge into</button>
                          )
                        ) : (
                          <button onClick={() => startMerge(c)} className="text-[11px] px-2 py-1 text-[#6B7280] hover:bg-[#F7F5F2] rounded flex items-center gap-1" data-testid={`start-merge-${c.key}`} title="Absorb this channel into another"><Merge size={11} /></button>
                        )}
                        <button onClick={() => toggleHide(c)} className="text-[11px] px-2 py-1 text-[#6B7280] hover:bg-[#F7F5F2] rounded" data-testid={`toggle-hide-${c.key}`} title={c.hidden ? 'Show' : 'Hide'}>
                          {c.hidden ? <Eye size={11} /> : <EyeOff size={11} />}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
